import { AgendaItemDoc, AgendaItemWithTimes } from '@livekatsomo/models';
import { useDateFns } from '@livekatsomo/web/hooks';
import MoreVert from '@mui/icons-material/MoreVert';
import IconButton from '@mui/material/IconButton';
import ListItem from '@mui/material/ListItem';
import ListItemButton, {
  ListItemButtonProps,
} from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import { forwardRef } from 'react';

/**
 * Props for the AgendaListItem component.
 */
export interface AgendaListItemProps extends ListItemButtonProps<'div'> {
  /**
   * The agenda item to display.
   */
  agendaItem: AgendaItemWithTimes;
  /**
   * Function to call when the menu button is clicked.
   * @param event - The mouse event that triggered the click.
   * @param agendaItem - The agenda item associated with the clicked menu button.
   */
  onMenuClick?: (
    event: React.MouseEvent<HTMLElement>,
    agendaItem: AgendaItemDoc,
  ) => void;
  /**
   * Whether to show the menu button.
   */
  showMenu?: boolean;
  /**
   * Whether the item is currently active.
   */
  active?: boolean;
}

/**
 * A component that renders a single item in the agenda list for the admin view.
 *
 * @param agendaItem - The agenda item to render.
 * @param onClick - The function to call when the item is clicked.
 * @param onMenuClick - The function to call when the menu button is clicked.
 * @param showMenu - Whether to show the menu button or not.
 * @param active - Whether the item is currently active or not.
 * @param sx - Additional styles to apply to the component.
 */
export const AgendaAdminCardListItem = forwardRef<
  HTMLDivElement,
  AgendaListItemProps
>(({ agendaItem, onMenuClick, showMenu, active, sx, ...props }, ref) => {
  const { format } = useDateFns();

  const title = agendaItem.timestamp
    ? `${format(agendaItem.startTime, 'HH:mm')} - ${format(
        agendaItem.endTime,
        'HH:mm',
      )} [${format(agendaItem.timestamp, 'HH:mm')}] ${agendaItem.title}`
    : `${format(agendaItem.startTime, 'HH:mm')} - ${format(
        agendaItem.endTime,
        'HH:mm',
      )} ${agendaItem.title}`;

  return (
    <ListItemButton ref={ref} {...props}>
      <ListItem
        sx={{
          opacity: agendaItem.disabled ? 0.5 : 1,
          backgroundColor: (theme) =>
            active ? theme.palette.grey[800] : 'inherit',
          ...sx,
        }}
        secondaryAction={
          showMenu ? (
            <IconButton
              edge="end"
              onClick={(event) => {
                event.stopPropagation();
                onMenuClick && onMenuClick(event, agendaItem);
              }}
            >
              <MoreVert />
            </IconButton>
          ) : null
        }
      >
        <ListItemText
          data-chromatic="ignore"
          primary={title}
          secondary={agendaItem.description}
        />
      </ListItem>
    </ListItemButton>
  );
});
